// modules/reportePagos/pages/ReportePagosDetalleTipoPage.jsx
// Migracion de busqueda.php (botones "Mostrar Reporte"/"Factura" del bloque
// "Reporte por Tipo y Año" -> cinco / cinco_2): listado fila por fila de
// cobros de Franquicia, Intercambio de Servicio o Castigo.
//
// A diferencia de "Detalle de Cobros" (pensado para Ingresos normales, con
// CUF/comisión/ejecutivo de venta/contrato), este trae los campos que le
// interesaban a estos tres tipos en el reporte legado: No. Autorización y
// Código de Control (del sistema viejo de dosificación, tabla `llave` — solo
// aplica a facturas anteriores a 2023) y moneda/sucursal de facturación.
import React, { useMemo, useState } from "react";
import {
  Box, Button, TextField, MenuItem, Typography, Alert, CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper,
  TableSortLabel,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";
import { getDetalleTipoIngreso } from "../services/reportePagosService";

const StyledHeaderCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  fontSize: "0.72rem",
  whiteSpace: "nowrap",
  padding: "3px 6px",
  backgroundColor: theme.palette.mode === "dark"
    ? theme.palette.grey[800] : theme.palette.grey[100],
}));

const compactFieldSx = {
  "& .MuiInputBase-root": { minHeight: 32, fontSize: "0.8rem" },
  "& .MuiInputBase-input": { py: 0.65 },
  "& .MuiInputLabel-root": { fontSize: "0.78rem" },
  "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
};

const celdaTruncada = { maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

const SUCURSALES = ["Todo", "La Paz", "Cochabamba", "Santa Cruz"];
const MESES = [
  { value: "Todo", label: "Todo (año completo)" },
  { value: 1, label: "Enero" }, { value: 2, label: "Febrero" },
  { value: 3, label: "Marzo" }, { value: 4, label: "Abril" },
  { value: 5, label: "Mayo" }, { value: 6, label: "Junio" },
  { value: 7, label: "Julio" }, { value: 8, label: "Agosto" },
  { value: 9, label: "Septiembre" }, { value: 10, label: "Octubre" },
  { value: 11, label: "Noviembre" }, { value: 12, label: "Diciembre" },
];
const TIPOS_DETALLE = [
  { value: "2", label: "Franquicia" },
  { value: "3", label: "Intercambio de Servicios" },
  { value: "4", label: "Castigo" },
];

const anioActual = new Date().getFullYear();
const ANIOS = Array.from({ length: 12 }, (_, i) => anioActual + 1 - i);

const formatMoneda = (value) =>
  Number(value || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatFecha = (value) => {
  const m = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return "-";
  return `${m[3]}/${m[2]}/${m[1]}`;
};

const COLUMNAS = [
  { col: "num", label: "Num" },
  { col: "cliente", label: "Cliente" },
  { col: "grupo", label: "Grupo" },
  { col: "nombreServicio", label: "Nombre" },
  { col: "facNombre", label: "Nombre Facturación" },
  { col: "servicio", label: "Servicio" },
  { col: "descripcion", label: "Descrip. Serv." },
  { col: "numPreFactura", label: "N° Pre-Factura" },
  { col: "numFactura", label: "N° Factura" },
  { col: "fechaEmision", label: "Fecha Emisión" },
  { col: "control", label: "Cód. Control" },
  { col: "autorizacion", label: "No. Autorización" },
  { col: "mesServicio", label: "Mes de Servicio" },
  { col: "bolivianos", label: "Monto Bs", align: "right" },
  { col: "dolares", label: "Monto $us", align: "right" },
  { col: "monedaFactura", label: "Moneda Factura" },
  { col: "sucursalFacturacion", label: "Sucursal Facturación" },
  { col: "bancoPago", label: "Banco Pago" },
  { col: "numRecibo", label: "N° Recibo" },
  { col: "numCheque", label: "N° Cheque" },
  { col: "fechaDeposito", label: "Fecha Depósito" },
  { col: "observacion", label: "Observación" },
  { col: "sucursal", label: "Sucursal" },
  { col: "cobrador", label: "Cobrador" },
];

const getValorOrdenable = (row, col) => {
  if (col === "fechaEmision" || col === "fechaDeposito") {
    return row[col] ? new Date(row[col]).getTime() : 0;
  }
  return row[col];
};

const compararValores = (a, b) => {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (typeof a === "string" || typeof b === "string") {
    return String(a).localeCompare(String(b), "es", { numeric: true, sensitivity: "base" });
  }
  return a - b;
};

const SortableHeaderCell = ({ col, label, align, orderBy, orderDir, onSort }) => (
  <StyledHeaderCell align={align} sortDirection={orderBy === col ? orderDir : false}>
    <TableSortLabel
      active={orderBy === col}
      direction={orderBy === col ? orderDir : "asc"}
      onClick={() => onSort(col)}
    >
      {label}
    </TableSortLabel>
  </StyledHeaderCell>
);

const ReportePagosDetalleTipoPage = () => {
  const [sucursal, setSucursal] = useState("Todo");
  const [mes, setMes] = useState("Todo");
  const [year, setYear] = useState(anioActual);
  const [tipoIngreso, setTipoIngreso] = useState("2");
  const [items, setItems] = useState([]);
  const [totales, setTotales] = useState({ bolivianos: 0, dolares: 0 });
  const [buscado, setBuscado] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [orderBy, setOrderBy] = useState(null);
  const [orderDir, setOrderDir] = useState("asc");

  const handleSort = (col) => {
    if (orderBy === col) {
      setOrderDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setOrderBy(col);
      setOrderDir("asc");
    }
  };

  const itemsOrdenados = useMemo(() => {
    if (!orderBy) return items;
    const copia = [...items].sort((a, b) =>
      compararValores(getValorOrdenable(a, orderBy), getValorOrdenable(b, orderBy))
    );
    if (orderDir === "desc") copia.reverse();
    return copia;
  }, [items, orderBy, orderDir]);

  const handleBuscar = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await getDetalleTipoIngreso({ sucursal, year, mes, tipoIngreso });
      setItems(Array.isArray(data?.items) ? data.items : []);
      setTotales(data?.totales || { bolivianos: 0, dolares: 0 });
      setBuscado(true);
    } catch (e) {
      setItems([]);
      setError(e?.response?.data?.error || "No se pudo generar el reporte.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <PageHeader
        title="Detallado por Tipo"
        subtitle="Cobros de Franquicia, Intercambio de Servicios o Castigo, fila por fila"
        backTo="/reporte-facturas"
      />
      <Box sx={{ p: 1 }}>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1, flexWrap: "wrap" }}>
          <TextField
            select size="small" label="Tipo" sx={{ ...compactFieldSx, width: 210 }}
            value={tipoIngreso} onChange={(e) => setTipoIngreso(e.target.value)}
          >
            {TIPOS_DETALLE.map((t) => <MenuItem key={t.value} value={t.value} sx={{ fontSize: "0.85rem" }}>{t.label}</MenuItem>)}
          </TextField>
          <TextField
            select size="small" label="Sucursal" sx={{ ...compactFieldSx, width: 150 }}
            value={sucursal} onChange={(e) => setSucursal(e.target.value)}
          >
            {SUCURSALES.map((s) => <MenuItem key={s} value={s} sx={{ fontSize: "0.85rem" }}>{s}</MenuItem>)}
          </TextField>
          <TextField
            select size="small" label="Mes" sx={{ ...compactFieldSx, width: 190 }}
            value={mes} onChange={(e) => setMes(e.target.value)}
          >
            {MESES.map((m) => <MenuItem key={m.value} value={m.value} sx={{ fontSize: "0.85rem" }}>{m.label}</MenuItem>)}
          </TextField>
          <TextField
            select size="small" label="Año" sx={{ ...compactFieldSx, width: 110 }}
            value={year} onChange={(e) => setYear(Number(e.target.value))}
          >
            {ANIOS.map((a) => <MenuItem key={a} value={a} sx={{ fontSize: "0.85rem" }}>{a}</MenuItem>)}
          </TextField>
          <Button
            size="small" variant="contained"
            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <SearchIcon />}
            onClick={handleBuscar} disabled={loading} sx={{ minHeight: 32 }}
          >
            {loading ? "Buscando..." : "Buscar"}
          </Button>
          {buscado && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              Total: {items.length}
            </Typography>
          )}
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!buscado ? (
          <Alert severity="info">Elige tipo, sucursal, mes y año, y presiona Buscar.</Alert>
        ) : !loading && items.length === 0 ? (
          <Alert severity="info">No hay cobros para los filtros seleccionados.</Alert>
        ) : (
          <>
            <Paper variant="outlined">
              <TableContainer sx={{ maxHeight: "65vh" }}>
                <Table size="small" stickyHeader sx={{ "& td": { py: 0.35, px: 0.75, fontSize: "0.74rem", whiteSpace: "nowrap" } }}>
                  <TableHead>
                    <TableRow>
                      {COLUMNAS.map((c) => (
                        <SortableHeaderCell key={c.col} {...c} orderBy={orderBy} orderDir={orderDir} onSort={handleSort} />
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={COLUMNAS.length} align="center" sx={{ py: 3 }}>
                          <CircularProgress size={24} />
                        </TableCell>
                      </TableRow>
                    ) : (
                      itemsOrdenados.map((r) => (
                        <TableRow key={r.num} hover>
                          <TableCell>{r.num}</TableCell>
                          <TableCell sx={celdaTruncada} title={r.cliente}>{r.cliente}</TableCell>
                          <TableCell>{r.grupo || "-"}</TableCell>
                          <TableCell sx={celdaTruncada} title={r.nombreServicio}>{r.nombreServicio || "-"}</TableCell>
                          <TableCell sx={celdaTruncada} title={r.facNombre}>{r.facNombre || "-"}</TableCell>
                          <TableCell>{r.servicio}</TableCell>
                          <TableCell sx={celdaTruncada} title={r.descripcion}>{r.descripcion}</TableCell>
                          <TableCell>{r.numPreFactura || "-"}</TableCell>
                          <TableCell>{r.numFactura || "-"}</TableCell>
                          <TableCell>{formatFecha(r.fechaEmision)}</TableCell>
                          <TableCell>{r.control || "-"}</TableCell>
                          <TableCell>{r.autorizacion || "-"}</TableCell>
                          <TableCell>{r.mesServicio || "-"}</TableCell>
                          <TableCell align="right">{formatMoneda(r.bolivianos)}</TableCell>
                          <TableCell align="right">{formatMoneda(r.dolares)}</TableCell>
                          <TableCell>{r.monedaFactura || "-"}</TableCell>
                          <TableCell>{r.sucursalFacturacion || "-"}</TableCell>
                          <TableCell>{r.bancoPago || "-"}</TableCell>
                          <TableCell>{r.numRecibo || "-"}</TableCell>
                          <TableCell>{r.numCheque || "-"}</TableCell>
                          <TableCell>{formatFecha(r.fechaDeposito)}</TableCell>
                          <TableCell sx={celdaTruncada} title={r.observacion}>{r.observacion || "-"}</TableCell>
                          <TableCell>{r.sucursal}</TableCell>
                          <TableCell>{r.cobrador || "-"}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            <Paper variant="outlined" sx={{ mt: 1.5, p: 1.5, maxWidth: 320, ml: "auto" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">Total bolivianos</Typography>
                <Typography variant="body2" fontWeight={700}>{formatMoneda(totales.bolivianos)}</Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
                <Typography variant="body2">Total dólares</Typography>
                <Typography variant="body2" fontWeight={700}>{formatMoneda(totales.dolares)}</Typography>
              </Box>
            </Paper>
          </>
        )}
      </Box>
    </DashboardLayout>
  );
};

export default ReportePagosDetalleTipoPage;
